import { useCallback, useEffect, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';

type ThemeSnapshot = {
  theme: ThemePreference;
  resolvedTheme: 'light' | 'dark';
  isDark: boolean;
};

const STORAGE_KEY = 'waiter-theme';
const listeners = new Set<(theme: ThemePreference) => void>();

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const resolve = (theme: ThemePreference): 'light' | 'dark' =>
  theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme;

let currentTheme: ThemePreference = (() => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  const stored = window.localStorage.getItem(STORAGE_KEY) as ThemePreference | null;
  const initial = stored ?? 'dark';
  window.localStorage.setItem(STORAGE_KEY, initial);
  return initial;
})();

const apply = (theme: 'light' | 'dark') => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.classList.toggle('dark', theme === 'dark');
};

apply(resolve(currentTheme));

const setPreference = (next: ThemePreference) => {
  currentTheme = next;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, next);
  }

  apply(resolve(next));
  listeners.forEach((listener) => listener(next));
};

export function useTheme(): ThemeSnapshot & {
  setTheme: (theme: ThemePreference) => void;
  toggle: () => void;
} {
  const [theme, setThemeState] = useState<ThemePreference>(currentTheme);
  const resolvedTheme = resolve(theme);

  useEffect(() => {
    const handle = (value: ThemePreference) => setThemeState(value);
    listeners.add(handle);
    return () => {
      listeners.delete(handle);
    };
  }, []);

  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => apply(event.matches ? 'dark' : 'light');
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((value: ThemePreference) => {
    setPreference(value);
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme]);

  return {
    theme,
    resolvedTheme,
    isDark: resolvedTheme === 'dark',
    setTheme,
    toggle,
  };
}

export function __resetThemeForTests() {
  setPreference('dark');
}
